import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { POS_CUSTOM_FEES_DEFAULT, makePosCustomFeeType, type PosCustomFeeType } from '@mlm/shared';
import { Button, Card, CardContent, Checkbox, Input } from '@mlm/ui';
import { getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { hasPerm } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';

/**
 * POS custom fee types — the list the cashier picks from when adding a charge on
 * top of a sale. Saving needs the grantable `tenant.payment_methods.write`
 * (super_admin passes implicitly); same typed-editor pattern as
 * `pos-payment-methods.tsx`.
 *
 * Label only: the cashier still types the amount, because a delivery fee varies
 * by distance. What this fixes is the NAME — it used to be free text, so every
 * till spelled "Delivery" its own way and the charge could never be grouped.
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function PosCustomFeesPage() {
  const qc = useQueryClient();
  const canWrite = hasPerm('tenant.payment_methods.write');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [types, setTypes] = useState<PosCustomFeeType[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const cfg = q.data.pos_custom_fees ?? POS_CUSTOM_FEES_DEFAULT;
    setTypes(Array.isArray(cfg.types) ? clone(cfg.types) : []);
  }, [q.data]);

  const save = useMutation({
    meta: { successMessage: 'POS custom fees saved.' },
    mutationFn: () => patchTenantFeatures({ pos_custom_fees: { types } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateType(i: number, patch: Partial<PosCustomFeeType>) {
    setTypes((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function addType() {
    setTypes((ts) => [...ts, makePosCustomFeeType()]);
  }
  function removeType(i: number) {
    setTypes((ts) => ts.filter((_, idx) => idx !== i));
  }

  function validate(): string | null {
    const codes = types.map((t) => t.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every fee needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Fee codes must be unique.';
    else if (types.some((t) => t.label.trim() === '')) msg = 'Every fee needs a label.';
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">POS custom fees</h1>
        <p className="text-sm text-muted-foreground">
          The charges a cashier can add on top of a sale — delivery, handling, packaging. The
          cashier picks the fee from this list and types the amount, so the wording is the same on
          every receipt from every till. A sale can carry several fees, but only one of each type.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          With no fees listed, the custom-fee box is hidden at the till entirely. Removing or
          disabling a fee stops it being charged from now on; receipts already printed keep the
          wording they were sold with.
        </p>
        {!canWrite && (
          <p className="mt-1 text-xs text-muted-foreground">
            Read-only. Saving needs the &ldquo;Edit payment methods &amp; POS fees&rdquo;
            permission (<span className="font-mono">tenant.payment_methods.write</span>).
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {types.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No custom fees yet.{' '}
                {canWrite
                  ? 'Add one below.'
                  : 'An admin with the payment-methods edit permission can add them.'}{' '}
                The cashier sees no custom-fee box until at least one is enabled.
              </CardContent>
            </Card>
          )}

          {types.map((t, i) => (
            <FeeTypeCard
              key={i}
              type={t}
              canWrite={canWrite}
              onChange={(patch) => updateType(i, patch)}
              onRemove={() => removeType(i)}
            />
          ))}

          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addType}>
                <Plus className="h-4 w-4" /> Add fee
              </Button>
              <ConfirmSaveButton
                size="sm"
                label="Save"
                title="Save POS custom fees"
                current={q.data?.pos_custom_fees}
                next={{ types }}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {save.isSuccess && !save.isPending && (
                <span className="text-xs text-success">Saved.</span>
              )}
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.error && (
                <span className="text-xs text-destructive">Save failed. Try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FeeTypeCard({
  type: t,
  canWrite,
  onChange,
  onRemove,
}: {
  type: PosCustomFeeType;
  canWrite: boolean;
  onChange: (patch: Partial<PosCustomFeeType>) => void;
  onRemove: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={t.code}
              disabled={!canWrite}
              placeholder="delivery"
              onChange={(e) => onChange({ code: e.target.value.trim() })}
            />
            <p className="text-[11px] text-muted-foreground">
              Stored on the sale. Changing it later leaves past sales pointing at the old one.
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Label</label>
            <Input
              value={t.label}
              disabled={!canWrite}
              placeholder="Delivery"
              onChange={(e) => onChange({ label: e.target.value })}
            />
            <p className="text-[11px] text-muted-foreground">What prints on the receipt.</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={t.enabled}
              disabled={!canWrite}
              onCheckedChange={(v) => onChange({ enabled: v === true })}
            />
            Enabled
          </label>
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
